import { css, jsx } from '@emotion/react';
import { Button } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { SIDEBAR_ID } from '../constants';
import { UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH, UNIVERSAL_SIDEBAR_WIDTH, } from '../../../constants';
import { Divider } from '../Divider';
import OutOfAppLinks from './OutOfAppLinks';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { useUniversalNavContext } from '../../../hooks';
const GAP = 20;
const FooterStyles = {
    sidebarCtaButtons: css `
    display: grid;
    grid-template-columns: 1fr fr;
    gap: 10px;
  `,
};
const Styles = labelStyles('OutOfAppSidebar', {
    root: ({ isCollapsed, isFullWidth, isShadowed, }) => [
        {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',
            padding: '30px 10px 0 10px',
            willChange: 'transform, width',
        },
        !isFullWidth &&
            withModifier('constrainedWidth', {
                width: UNIVERSAL_SIDEBAR_WIDTH,
                maxWidth: '100vw',
            }),
        !isFullWidth &&
            withModifier('bordered', {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 1,
                    backgroundColor: 'var(--zds-background-strongest, #ECE9DF)',
                },
            }),
        isShadowed &&
            withModifier('shadowed', {
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '100%',
                    bottom: 0,
                    width: 10,
                    pointerEvents: 'none',
                    background: `linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.10) 0%,
            rgba(0, 0, 0, 0.03) 50%,
            rgba(0, 0, 0, 0.00) 100%
          )`,
                },
            }),
        isCollapsed &&
            withModifier('collapsed', {
                width: UNIVERSAL_SIDEBAR_COLLAPSED_WIDTH,
                overflow: 'visible',
            }),
    ],
    header: {
        flex: 'none',
    },
    content: ({ isCollapsed, isV3, }) => [
        {
            overflow: 'auto',
            paddingTop: isV3 ? 5 : GAP,
            paddingBottom: 5,
            flexBasis: 'auto',
            flexShrink: 1,
            flexGrow: 0,
            scrollbarWidth: 'none',
            '::-webkit-scrollbar': {
                display: 'none',
            },
        },
        isCollapsed &&
            withModifier('collapsed', {
                overflow: 'visible',
            }),
    ],
    links: ({ isCollapsed, isV3, }) => [
        Object.assign({ alignContent: 'start', display: 'grid', gap: isV3 ? 5 : 10, listStyle: 'none' }, (!isCollapsed && { padding: '0 2px' })),
    ],
    footer: {
        flex: 'none',
        paddingBottom: GAP,
    },
});
const OutOfAppSidebar = ({ callToAction, currentUrl, isActuallyCollapsed, isCollapsed = false, isFullWidth = true, isOnAppPage, isLoggedIn, isShadowed, isV3 = false, loginHref, onClick, onMouseEnter, onMouseLeave, outOfAppLinks, signupHref, size, }) => {
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const mouseProps = {
        onClick: onClick,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
    };
    const Footer = () => {
        const LOGIN_TEXT = 'Log in';
        const MY_ZAPS_TEXT = 'My Zaps';
        const SIGN_UP_TEXT = 'Sign up';
        const ZAPS_URL = getFullyQualifiedURL('/app/zaps');
        const LOGIN_URL = getFullyQualifiedURL(loginHref);
        const SIGNUP_URL = getFullyQualifiedURL(signupHref);
        const callToActionUrl = getFullyQualifiedURL(`${callToAction === null || callToAction === void 0 ? void 0 : callToAction.href}?demo_source=cs_nav_header_${currentUrl}`) || '';
        const handleCallToActionClick = (text) => {
            emitUserInteractionEvent({
                event_action: 'click',
                option_selected: `UniversalDynamicTopbar: ${text}`,
            });
        };
        if (size === 'small' && !isActuallyCollapsed) {
            if (!isLoggedIn) {
                return (jsx("div", { css: Styles.footer, role: "region", "data-zds": true },
                    jsx(Divider, null),
                    jsx("div", { css: FooterStyles.sidebarCtaButtons, "data-zds": true },
                        callToAction && (jsx(Button, { color: "secondary", href: callToActionUrl, onClick: () => handleCallToActionClick(`${callToAction === null || callToAction === void 0 ? void 0 : callToAction.text} to ${callToActionUrl}`), size: "small" }, callToAction.text)),
                        jsx(Button, { color: "secondary", href: LOGIN_URL, size: "small", onClick: () => handleCallToActionClick(`${LOGIN_TEXT} ${LOGIN_URL}`) }, LOGIN_TEXT),
                        jsx(Button, { color: "primary", href: SIGNUP_URL, size: "small", onClick: () => handleCallToActionClick(`${SIGN_UP_TEXT} ${SIGNUP_URL}
                      )}`) }, SIGN_UP_TEXT))));
            }
            if (isLoggedIn && !isOnAppPage) {
                return (jsx("div", { css: Styles.footer, role: "region", "data-zds": true },
                    jsx(Divider, null),
                    jsx("div", { css: FooterStyles.sidebarCtaButtons, "data-zds": true },
                        callToAction && (jsx(Button, { color: "secondary", href: callToActionUrl, isFullWidth: true, size: "medium", onClick: () => handleCallToActionClick(`${callToAction === null || callToAction === void 0 ? void 0 : callToAction.text} to ${callToActionUrl}`) }, callToAction.text)),
                        jsx(Button, { color: "primary-alt", href: ZAPS_URL, isFullWidth: true, size: "medium", onClick: () => handleCallToActionClick(`${MY_ZAPS_TEXT} ${ZAPS_URL}`) }, MY_ZAPS_TEXT))));
            }
        }
        return null;
    };
    return (jsx("nav", Object.assign({ "aria-label": "Site Links", css: Styles.root({ isCollapsed, isFullWidth, isShadowed }), "data-testid": "universalSidebar", id: SIDEBAR_ID }, mouseProps, { "data-zds": true }),
        jsx("div", { css: Styles.content({ isCollapsed, isV3 }), "data-zds": true },
            jsx("ul", { css: Styles.links({ isCollapsed, isV3 }), "data-zds": true },
                jsx(OutOfAppLinks, { isCollapsed: isCollapsed, navigationItems: outOfAppLinks }))),
        jsx(Footer, null)));
};
export default OutOfAppSidebar;
//# sourceMappingURL=OutOfAppSidebar.js.map
// @zapier/universal-layout@9.12.4 (a02ac1bd0)
